/**
 * Pendo instrumentation for Report Center Beta (spec 094).
 *
 * ── The gate ────────────────────────────────────────────────────────────────
 * Everything here is gated on `VITE_PENDO_API_KEY`. Absent => every export is a
 * no-op: no script tag, no network, no `window.pendo`, no event listeners. That
 * keeps localhost, unrelated tenants and the whole test suite clean by default.
 *
 * The key is read LAZILY (`apiKey()`), not captured in a module-level const. A
 * const would be frozen at module-eval time, which makes the no-op path
 * untestable — `vi.stubEnv` could never flip it. An untested no-op path is the
 * same class of problem as a fail-soft `catch` you cannot observe.
 *
 * NOTE ON THE KEY: the Pendo *agent* API key is a PUBLIC client-side
 * identifier — it ships inside the page on every Pendo install, like a GA
 * measurement id. It is NOT the Pendo *integration* key used for the
 * Aggregations API, which is a real secret and must never appear in this
 * bundle, in a fixture, or on a command line.
 *
 * ── Why this module imports nothing from the app ────────────────────────────
 * `src/App.jsx` imports every page eagerly, so it is the root chunk. Importing
 * `reportGridUtils` (for `resolveFilterLabel`) or `pages/reports/registry`
 * (which statically pulls in all 24 specialized report components) from here
 * would drag that graph into the root AND open a real circular-import hazard:
 * a component that calls `trackTransform` while this module is still evaluating
 * would see a half-initialised binding. So the report-shaped derivations —
 * `filterNames`, `resultShape`, `reportSurface` — live in `./reportFacts.js`,
 * which is only imported by code that already owns those chunks.
 *
 * ── The rate cap, and why there is a send queue ─────────────────────────────
 * HARD PENDO LIMIT: at most ONE Track Event per visitor per second. Over that,
 * events are dropped SILENTLY — and a silently dropped event is
 * indistinguishable from a feature nobody used.
 *
 * Debouncing UI transforms alone does not solve this, which is where both plan
 * documents stop short. Two concrete collisions survive a per-transform
 * debounce:
 *
 *   1. A report that finishes in under a second (`1 <2s` is an entire duration
 *      bucket) emits `Report Run Started` and `Report Run Completed` inside the
 *      same second. One of the pair is dropped, EVERY time, on EVERY fast
 *      report — so started/completed counts would systematically disagree and
 *      read as a failure rate that does not exist.
 *   2. Run, then click Export as soon as the grid paints. Two different event
 *      names, two different debounce keys, same second.
 *
 * So every event leaves through `enqueue()`, a FIFO drained at no more than one
 * send per MIN_SEND_GAP_MS. Coalescing (trackTransform) and rate limiting
 * (enqueue) are separate jobs and are separated here.
 *
 * ── PII: this is a financial application ────────────────────────────────────
 * It holds law-firm client, matter, timekeeper and compensation data. Report
 * cells are WIP and A/R figures.
 *
 *   - NEVER a filter VALUE. Filter NAMES and low-cardinality enum tokens only.
 *   - NEVER an error message or stack — an `errorClass` enum instead.
 *   - NEVER an email or display name — the bare CB user id (spec 097).
 *   - `role` IS sent: the firm's own CB StaffCategory job label. A job
 *     category, not personal data — and never an email, name or filter value.
 *   - NEVER a report cell value.
 *
 * `sanitizeProps` enforces the shape half of that by construction: only
 * strings, finite numbers and booleans survive. An object or array — the way a
 * filter map would realistically leak — is dropped rather than serialised.
 */

/** Lazily read so `vi.stubEnv` works and the no-op path is testable. */
const apiKey = () => import.meta.env.VITE_PENDO_API_KEY || ''

/** True when instrumentation is switched on for this build. */
export const pendoEnabled = () => Boolean(apiKey())

/**
 * Which deployment this is, for Pendo account metadata.
 *
 * NOT `import.meta.env.MODE`, which the tracking plan's identity model uses.
 * Nothing in this repo passes `--mode`, so MODE is Vite's default and resolves
 * to 'production' for BOTH `cb2` and `cb2-qa` — the two CI builds are plain
 * `npm run build`. Using it would label QA traffic as production and make the
 * environment segment (the thing that lets one Pendo app serve both) useless
 * for exactly the purpose it exists.
 *
 * The hostname does distinguish them: cb2 is served from *.centerbase.us on QA
 * and *.centerbase.ai on prod (see .github/workflows/deploy-{qa,prod}.yml,
 * which set VITE_PATH_TENANT_HOST to reports.centerbase.us / .ai). Deriving it
 * needs no new env var and cannot drift out of sync with a deploy.
 */
export function pendoEnvironment(hostname) {
  const host = String(
    hostname ?? (typeof window !== 'undefined' ? window.location?.hostname : '') ?? ''
  ).toLowerCase()
  if (!host) return 'unknown'
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) return 'local'
  if (host.endsWith('.centerbase.us')) return 'qa'
  if (host.endsWith('.centerbase.ai')) return 'production'
  return 'unknown'
}

/**
 * Minimum spacing between sends. Pendo's cap is one per second; 1100ms leaves
 * headroom for clock skew rather than sitting exactly on the boundary.
 */
const MIN_SEND_GAP_MS = 1100

/** Trailing debounce for a burst of the same transform on the same report. */
const TRANSFORM_DEBOUNCE_MS = 1200

/**
 * Backstop so a runaway loop cannot grow the queue without bound. Reaching this
 * would itself be a bug; dropping the OLDEST keeps the most recent behaviour,
 * which is the more useful half.
 */
const MAX_QUEUE = 200

let loaded = false
let initialized = false
let listenersBound = false

// ── Property hygiene ────────────────────────────────────────────────────────

/**
 * Keep only scalar property values. This is a confidentiality guard, not
 * tidiness: the realistic way a filter VALUE leaks into Pendo is somebody
 * passing an object (a filter map, a definition, an error) as a property, and
 * a structural drop makes that impossible instead of merely discouraged.
 *
 * NaN/Infinity go too — Pendo stores them as nulls that look like real zeroes.
 */
export function sanitizeProps(props) {
  const out = {}
  if (!props || typeof props !== 'object') return out
  for (const [k, v] of Object.entries(props)) {
    if (typeof v === 'string') { out[k] = v; continue }
    if (typeof v === 'boolean') { out[k] = v; continue }
    if (typeof v === 'number' && Number.isFinite(v)) { out[k] = v; continue }
    // Everything else — object, array, function, symbol, null, undefined,
    // NaN, Infinity — is deliberately omitted.
  }
  return out
}

// ── Derived, GROUPABLE properties ───────────────────────────────────────────
// Pendo's Data Explorer cannot Group By a numeric or time property, only
// strings and booleans. So every numeric we care about ships beside a string
// bucket, and every bucket label carries a leading ordinal because Pendo groups
// alphabetically — "10-60s" would otherwise sort between "1-5m" and "2-10s".
//
// These label strings are a wire contract: Track Events are NOT retroactive, so
// renaming a bucket later splits the historical series in two. Change them only
// deliberately.

/** Zero-padded LOCAL hour "00".."23". Local, not UTC — a firm's 9am is the question. */
export const localHour = (d = new Date()) => String(d.getHours()).padStart(2, '0')

/** ISO-ordered so Data Explorer's alphabetical Group By reads Mon→Sun. */
const DAYS = ['7-Sun', '1-Mon', '2-Tue', '3-Wed', '4-Thu', '5-Fri', '6-Sat']
export const dayOfWeek = (d = new Date()) => DAYS[d.getDay()]

/**
 * Boundaries straddle MAX_RUN_MS (6 min — the job cap, which mirrors CB's 300s
 * SQL CommandTimeout) so a run that died on the timeout lands in its own band
 * instead of blurring into "slow".
 */
export function durationBucket(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '9 unknown'
  if (ms < 2_000) return '1 <2s'
  if (ms < 10_000) return '2 2-10s'
  if (ms < 60_000) return '3 10-60s'
  if (ms < 300_000) return '4 1-5m'
  if (ms < 360_000) return '5 5-6m'
  return '6 6m+'
}

/** '0 empty' is kept distinct: an empty result is an answer, not a missing number. */
export function rowCountBucket(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '9 unknown'
  if (n === 0) return '0 empty'
  if (n < 100) return '1 1-99'
  if (n < 1_000) return '2 100-999'
  if (n < 10_000) return '3 1k-10k'
  return '4 10k+'
}

// ── Agent load ──────────────────────────────────────────────────────────────

/**
 * Injects Pendo's agent once, with its own call-queueing stub so calls made
 * before pendo.js lands are replayed rather than lost.
 *
 * Loaded from here rather than inlined in index.html so the env gate lives in
 * exactly one place. index.html stays free of third-party tags.
 */
function loadAgent() {
  const key = apiKey()
  if (!key || loaded) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  loaded = true

  // Pendo's documented stub: window.pendo queues into _q until the real agent
  // replaces it. `initialize` unshifts so it is always replayed first.
  //
  // The method list matches Pendo's CURRENT install snippet exactly, including
  // `trackAgent`, which ours omitted. Nothing calls it today; the cost of the
  // gap is that anything which ever did — Pendo's own snippet, a Guide, a future
  // caller — would hit `undefined` in the window between this stub and the agent
  // arriving, rather than being queued. Cheaper to match the list than to
  // remember the exception.
  const p = window
  const o = (p.pendo = p.pendo || {})
  o._q = o._q || []
  for (const m of ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent']) {
    o[m] =
      o[m] ||
      function queued(...args) {
        o._q[m === 'initialize' ? 'unshift' : 'push']([m, ...args])
      }
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://cdn.pendo.io/agent/static/${encodeURIComponent(key)}/pendo.js`
  const first = document.getElementsByTagName('script')[0]
  if (first?.parentNode) first.parentNode.insertBefore(script, first)
  else document.head.appendChild(script)
}

// ── Send queue ──────────────────────────────────────────────────────────────

/** @type {Array<{name: string, props: object}>} */
const queue = []
let lastSentAt = 0
let drainTimer = null

/** The only place window.pendo.track is called. Never throws. */
function sendNow(name, props) {
  try {
    window.pendo.track(name, props)
    lastSentAt = Date.now()
  } catch {
    // Analytics must never break a report run, an export, or app boot.
  }
}

function drain() {
  drainTimer = null
  if (queue.length === 0) return
  const wait = MIN_SEND_GAP_MS - (Date.now() - lastSentAt)
  if (wait > 0) {
    drainTimer = setTimeout(drain, wait)
    return
  }
  const next = queue.shift()
  sendNow(next.name, next.props)
  if (queue.length > 0) drainTimer = setTimeout(drain, MIN_SEND_GAP_MS)
}

/**
 * Every event in this module goes through here. Sends immediately when the
 * rate budget allows, otherwise spaces the send out — see the header note on
 * why per-event debouncing is not enough.
 */
function enqueue(name, props) {
  if (!apiKey() || !initialized) return
  try {
    // `source` is stamped AFTER sanitizing, so a caller cannot override it.
    // cb-api stamps 'server' on its own events; having both makes the
    // client/server split checkable in Data Explorer instead of merely
    // asserted in a spec — and a regression into double-counting becomes
    // visible rather than silent.
    const clean = { ...sanitizeProps(props), source: 'client' }
    if (queue.length >= MAX_QUEUE) queue.shift()
    queue.push({ name, props: clean })
    if (drainTimer == null) drain()
  } catch {
    // Never let instrumentation surface to the user.
  }
}

/**
 * Send everything still buffered, right now, ignoring the rate gap.
 *
 * Used on pagehide only. A queued event at tab close is CERTAINLY lost; a
 * rate-limited one is only possibly dropped by Pendo. Flushing is strictly the
 * better of the two.
 */
function flushAll() {
  flushTransforms()
  if (drainTimer != null) { clearTimeout(drainTimer); drainTimer = null }
  while (queue.length > 0) {
    const next = queue.shift()
    sendNow(next.name, next.props)
  }
}

// ── Identity ────────────────────────────────────────────────────────────────

/**
 * Visitor id: the **bare `cbUserId`**.
 *
 * The CB user id is the `https://centerbase.com/cbuserid` claim — a GUID.
 * Measured against two live access tokens for two different vladdemo accounts
 * on 2026-08-24: the claim is present on the ACCESS token and distinct per
 * user. (A comment in `cb-api/src/legacy/mvc-proxy.js` still says the Auth0
 * Action sets it on the ID token only; that is out of date.)
 *
 * ── This used to be `${tenant}:${cbUserId}`. Do not put the prefix back. ──
 *
 * The prefix was added to prevent cross-tenant id collisions. That reasoning is
 * correct for a numeric id and unnecessary for a GUID, which is already globally
 * unique — so it bought nothing, and it cost something real:
 *
 *   **Visitors and Accounts are stored at the SUBSCRIPTION level.** A Pendo app
 *   partitions Pages, Features, Track Events and Guides — not Visitors. Report
 *   Center having its own app does not give it its own visitor list; it shares
 *   one with `Centerbase`, where legacy has been identifying these same humans
 *   by their bare `UserData.UserId` GUID since 2021
 *   (`centerbase/CenterbaseWeb/CenterbaseWeb/Scripts/CBNotificationHub.js:120`).
 *   A prefixed id therefore created a SECOND visitor record for one person.
 *
 *   That is not cosmetic. **Listen and NPS are subscription-level too**, and the
 *   beta feedback widget runs through Listen — so a firm's feedback would attach
 *   to the forked record rather than to the person. Pendo cannot merge visitor
 *   records retroactively, so this was free to fix now and unfixable later.
 *
 * `account.id` stays the bare `cbsite` tenant slug (see `initPendo`) and is NOT
 * changing. Tenant is still on every event as the account, so "one firm's
 * visitors" is a Group By, not a string prefix. Matching legacy's account format
 * (`VladDemo.centerbase.com`) was considered and deliberately rejected — no
 * cross-app comparison is intended.
 *
 * ⚠️ Residual risk, stated rather than papered over: a bare id IS collidable if
 * CB ever hands back an int sqlid instead of a GUID for `cbuserid`. It does not
 * today, on any measured token, and the `sub.split('|').pop()` fallback in
 * `./identity.js` is an Auth0 user id — also globally unique, because every CB
 * tenant shares ONE Auth0 tenant (`login.centerbase.com`, one client id for qa
 * and prod). If that ever changes, the fix is a new namespace, not this prefix:
 * re-adding it would fork every existing visitor a second time.
 *
 * ⚠️ cb-api's server-side events MUST produce this string byte for byte, or one
 * person becomes two Pendo visitors and every per-user number is wrong. That is
 * satisfied today by construction, not by luck: `cb-api/src/legacy/client.js`
 * derives `auth.userId` from the SAME `cbuserid` claim, and D1 `jobs.user_id`
 * is written from `auth.userId`. So `job.user_id` === this. Both sides changed
 * in one commit for exactly this reason, and a test pins them equal.
 * Exported so both sides can share one definition and a test can pin it.
 */
/**
 * The visitor id this module initialized with, so `setVisitorRole` can address
 * the same visitor without the caller having to re-supply an identity.
 */
let currentVisitorId = null

/**
 * Max length of a `role`. A firm types StaffCategory freely, and it ends up on a
 * third-party analytics wire; 100 is far past any real job title. Mirrors the
 * cap in `cb-api/src/domains/users/staffCategory.js` so neither side truncates
 * differently and splits one category into two in a Group By.
 */
const MAX_ROLE_LEN = 100

/**
 * `string | null`. Blank, non-string and whitespace all become null, which the
 * callers render as an ABSENT property rather than an empty one — an empty
 * string reads as data in Pendo, a missing property reads as missing.
 *
 * Deliberately NOT mapped to a taxonomy. The same job is "Partner", "Equity
 * Partner" and "PARTNER" across firms; normalising would fabricate a mapping
 * nobody agreed to. Expect variants and solve it in Pendo with the real values
 * in hand.
 */
function normalizeRole(role) {
  if (typeof role !== 'string') return null
  const s = role.trim()
  return s ? s.slice(0, MAX_ROLE_LEN) : null
}

// `tenant` is accepted and DELIBERATELY unused. Keeping the two-arg shape keeps
// every call site and cb-api's `pendoVisitorId(tenant, userId)` symmetric, so the
// test that pins "client format === server format" compares like with like — and
// so a future namespace change has somewhere to go. Dropping the parameter would
// make the two signatures diverge, which is how they drift apart unnoticed.
export const visitorId = (tenant, cbUserId) => String(cbUserId)

/**
 * Call once after auth resolves. Idempotent, and never anonymous.
 *
 * `isAdmin` is OPTIONAL and, when omitted, is left off the visitor entirely
 * rather than defaulted. See the note in the body — a property that is always
 * `false` is worse than an absent one, because Pendo will let you segment on it.
 *
 * `role` is OPTIONAL too, and is the firm's CB **StaffCategory** ("Partner",
 * "Associate", …) — see `setVisitorRole` below, which is how it normally
 * arrives, because resolving it needs a network round trip and initialize must
 * never wait for one.
 *
 * @param {{tenant: string, cbUserId: string|number, role?: string, isAdmin?: boolean, environment?: string}} id
 * @returns {boolean} whether Pendo was initialized on this call
 */
export function initPendo({ tenant, cbUserId, role, isAdmin, environment } = {}) {
  if (!apiKey() || initialized) return false
  // Never initialize anonymous: an anonymous visitor is unattributable noise
  // that still consumes a Pendo seat.
  if (!tenant || cbUserId == null || cbUserId === '') return false

  loadAgent()
  if (typeof window === 'undefined' || !window.pendo) return false
  initialized = true
  currentVisitorId = visitorId(tenant, cbUserId)

  try {
    const visitor = {
      id: currentVisitorId,
      betaCohort: 'report-center-v2',
    }
    const cleanRole = normalizeRole(role)
    if (cleanRole) visitor.role = cleanRole
    /*
     * Admin-ness is only sent when it is actually KNOWN.
     *
     * The `https://centerbase.com/isadmin` claim is NOT minted on cb2's access
     * token — verified 2026-08-24 against live tokens for an admin account and
     * a non-admin account on vladdemo, and again on 2026-08-26: absent from
     * all. So `sessionService.isAdmin()` returns `false` for everyone, and
     * cb-api's `auth.isAdmin` reads the same absent claim. `/users/me` is no
     * better — it reports `{isAdmin:false, degraded:true}` for every caller
     * wherever `CB_AUTHORIZE_SECRET` is unbound, which includes cb-api-qa.
     *
     * Shipping `isAdmin: false` for the whole firm would look like a real
     * measurement and read as "this firm has no admins". Per the house rule for
     * a financial app — when in doubt, do not compute, surface — it is omitted,
     * and the gap is called out in spec 094 instead. The reliable predicate, if
     * this is ever wanted, is CB's own `Get_SettingsModel.canCustomize`.
     *
     * ⚠️ This branch NO LONGER sets `visitor.role` (spec 097). It used to map
     * `isAdmin → 'admin' | 'user'`, which was dead code — nothing ever passed
     * `isAdmin` — but leaving it would have made `role` carry two unrelated
     * vocabularies on one key, so a firm's Group By would mix "Partner" with
     * "admin". `role` now means StaffCategory and nothing else. Same reasoning
     * as `trigger` / `serverTrigger` in spec 095 §4.
     */
    if (typeof isAdmin === 'boolean') {
      visitor.isAdmin = isAdmin
    }

    window.pendo.initialize({
      visitor,
      account: {
        id: tenant,
        // Firm DISPLAY name is deliberately absent — swap it in only once
        // somebody has confirmed the DPA covers it.
        name: tenant,
        environment: environment || pendoEnvironment(),
        reportCenterBeta: true,
      },
    })
  } catch {
    // A failed initialize must not stop the app from booting.
  }

  bindListeners()
  return true
}

/**
 * Attach the visitor's `role` (CB StaffCategory) AFTER initialization.
 *
 * ── Why this is a second call and not an `initPendo` argument ────────────────
 *
 * Resolving StaffCategory needs a network round trip to CB (via `/users/me`),
 * and `pendo.initialize` must never wait on one. Blocking it would mean every
 * page view before the fetch resolves is either unattributed or not captured at
 * all, to add a nice-to-have segmentation property — and if CB were slow or
 * down, analytics would be silently off rather than merely role-less.
 *
 * So the ordering guarantee is STRUCTURAL rather than a matter of timing:
 * `App.jsx` initializes from the token (no I/O, same as before this change) and
 * only then fires the role fetch, which lands here whenever it lands. There is
 * no code path on which a role fetch can precede or delay `initialize`, and a
 * test asserts exactly that rather than measuring elapsed time.
 *
 * `updateOptions` merges visitor metadata on the existing visitor, so this adds
 * `role` without disturbing `betaCohort`, the account, or anything the agent has
 * already captured. Events sent before it arrives are attributed to the same
 * visitor id and pick the role up in Pendo's own visitor record.
 *
 * @param {string} role  CB StaffCategory. Blank/non-string is a no-op — an
 *   absent property must stay absent rather than becoming an empty one.
 * @returns {boolean} whether an update was actually sent
 */
export function setVisitorRole(role) {
  if (!apiKey() || !initialized || !currentVisitorId) return false
  const cleanRole = normalizeRole(role)
  if (!cleanRole) return false
  try {
    window.pendo.updateOptions({ visitor: { id: currentVisitorId, role: cleanRole } })
    return true
  } catch {
    // Analytics never surfaces to the user, and never blocks the app.
    return false
  }
}

/**
 * Tenant switch on the shared host (`setStoredSite`) does NOT re-login, so
 * without this the user's activity keeps landing on the previous firm's Pendo
 * account.
 */
export function switchAccount({ tenant, cbUserId, isAdmin } = {}) {
  if (!apiKey() || !initialized) return false
  if (!tenant || cbUserId == null || cbUserId === '') return false
  try {
    const visitor = { id: visitorId(tenant, cbUserId) }
    // Same rule as initPendo: only send admin-ness when it is actually known.
    if (typeof isAdmin === 'boolean') visitor.isAdmin = isAdmin
    window.pendo.updateOptions({
      visitor,
      account: { id: tenant, name: tenant },
    })
    return true
  } catch {
    return false
  }
}

// ── Named events ────────────────────────────────────────────────────────────

/** Properties shared by every run event. */
const runBase = (r = {}) => ({
  reportId: r.reportId,
  reportName: r.reportName,
  // 'specialized' | 'generic' — REGISTRY MEMBERSHIP, not actionKey truthiness.
  reportSurface: r.reportSurface,
  // CB's internal renderer key ('cbARReportView', '' for generic). No firm data
  // in it, and cb-api ships the same field, so client and server events join.
  actionKey: r.actionKey,
  reportType: r.reportType,
  // 'sync' | 'job' — the EXECUTION path actually taken. cb-api emits 'scheduled'.
  runMode: r.runMode,
  // Did the user hand this off to the queue rather than wait? Distinct from
  // runMode: with VITE_REPORT_JOBS on, every run is a job whether or not the
  // user chose to walk away.
  handoff: Boolean(r.handoff),
  // What caused the run. Most runs are auto-runs on open (runOnOpen), so
  // without this "most-run report" would just restate "most-visited page".
  trigger: r.trigger,
  /*
   * NOT defaulted. These used to be `?? 0` / `?? ''`, which is the
   * empty-vs-unknown confusion in miniature: the specialized seam cannot see a
   * report's filters at all, so a default would have reported "0 filters, none
   * used" for 21 reports as though it were a measurement. Undefined is dropped
   * by sanitizeProps, so an absent property reads as absent. The generic path
   * always supplies both, and a real 0 / '' still ships.
   */
  filterCount: r.filterCount,
  // NAMES ONLY. See reportFacts.filterNames.
  filtersUsed: r.filtersUsed,
  localHour: localHour(),
  dayOfWeek: dayOfWeek(),
})

/*
 * ── Who closes out a run: the double-counting rule ──────────────────────────
 *
 * cb-api's job runner emits `Report Run Completed` / `Report Run Failed` itself,
 * from every terminal write, for EVERY job — including one the user happens to
 * sit and watch in-tab (`cb-api/src/domains/reports/jobs/runner.js`, verified
 * on branch `feat/pendo-server-events`). So:
 *
 *   The SERVER owns runMode 'job' and 'scheduled'.
 *   The CLIENT owns runMode 'sync', and nothing else.
 *
 * `Report Run Started` stays client-side for everything: the server has no
 * equivalent, and started-vs-terminal is how run abandonment gets measured.
 *
 * But the runner deliberately does NOT send `resultShape` — it is a property of
 * the RENDERED result and the runner cannot know it. `VITE_REPORT_JOBS` is
 * 'true' on QA, so there every generic run is a job and obeying the rule
 * literally would make `resultShape` unmeasurable. (It is 'false' on prod
 * today, so prod runs are sync and the client owns them outright — which is
 * exactly why this cannot be handled by picking one of the two paths.)
 *
 * Hence `Report Result Rendered`: the browser reporting what the USER actually
 * saw, which is a genuinely different fact from what the server ran — a job can
 * complete with no tab open and never be rendered at all. One terminal client
 * event per run either way, so no run is ever counted twice.
 */

export const trackRunStarted = (r) => enqueue('Report Run Started', runBase(r))

/** SYNC RUNS ONLY. A job's completion belongs to cb-api — see the note above. */
export const trackRunCompleted = (r = {}) =>
  enqueue('Report Run Completed', {
    ...runBase(r),
    outcome: 'ready',
    // A property of the RESULT, knowable only once rows are back.
    resultShape: r.resultShape,
    durationMs: r.durationMs,
    durationBucket: durationBucket(r.durationMs),
    rowCount: r.rowCount,
    rowCountBucket: rowCountBucket(r.rowCount),
  })

/**
 * SYNC RUNS ONLY — see the note above.
 * errorClass is an ENUM. Never the message: it can carry firm data or a stack.
 */
export const trackRunFailed = (r = {}) =>
  enqueue('Report Run Failed', {
    ...runBase(r),
    outcome: 'failed',
    durationMs: r.durationMs,
    durationBucket: durationBucket(r.durationMs),
    errorClass: r.errorClass,
    httpStatus: r.httpStatus,
  })

/**
 * JOB RUNS ONLY. What the browser actually put on screen for a run whose
 * completion cb-api owns. Carries the client-only facts — above all
 * `resultShape` — without re-counting the run.
 *
 * `outcome: 'failed'` here means the USER did not get the result (the poll
 * threw, the enriched /data read 500'd on CB's file race, the tab lost the
 * network). The job itself may well be `ready` server-side; that divergence is
 * the interesting signal, not a contradiction.
 */
export const trackResultRendered = (r = {}) =>
  enqueue('Report Result Rendered', {
    ...runBase(r),
    // 'rendered' | 'failed'
    outcome: r.outcome,
    resultShape: r.resultShape,
    durationMs: r.durationMs,
    durationBucket: durationBucket(r.durationMs),
    rowCount: r.rowCount,
    rowCountBucket: rowCountBucket(r.rowCount),
    errorClass: r.errorClass,
    httpStatus: r.httpStatus,
  })

export const trackExport = (e = {}) =>
  enqueue('Report Export', {
    reportId: e.reportId,
    reportName: e.reportName,
    // 'csv' | 'xlsx' | 'pdf' | 'print'
    format: e.format,
    // 'server' | 'client' — the BRANCH TAKEN, not the button pressed. The
    // product question is whether users trust the authoritative server export
    // or prefer "export what you see"; a mislabelled engine answers it
    // backwards.
    engine: e.engine,
    // 'report_view' | 'specialized' | 'job_drawer'
    surface: e.surface,
    dataOnly: Boolean(e.dataOnly),
    honorColumns: Boolean(e.honorColumns),
    honorFilters: Boolean(e.honorFilters),
    rowCount: e.rowCount,
    rowCountBucket: rowCountBucket(e.rowCount),
    localHour: localHour(),
    dayOfWeek: dayOfWeek(),
  })

export const trackSavedView = (v = {}) =>
  enqueue('Saved View', {
    // 'save' | 'apply' | 'rename' | 'delete'
    action: v.action,
    reportId: v.reportId,
    reportName: v.reportName,
    // Scope token only ('private'|'group'|'firm'), never a group name.
    scope: v.scope,
    localHour: localHour(),
  })

export const trackJob = (j = {}) =>
  enqueue('Report Job', {
    // 'queued' | 'cancelled' | 'downloaded' | 'scheduled' | 'delivered'
    action: j.action,
    reportId: j.reportId,
    reportName: j.reportName,
    format: j.format,
    runMode: j.runMode,
    localHour: localHour(),
  })

// ── UI transforms: coalesced, then rate-limited ─────────────────────────────

/** key -> { props, count, timer } */
const pending = new Map()

function firePending(key) {
  const e = pending.get(key)
  if (!e) return
  if (e.timer != null) clearTimeout(e.timer)
  pending.delete(key)
  enqueue('Report UI Transform', {
    ...e.props,
    // How many actions the burst actually contained. Without it a 12-step drag
    // and a single click are the same event.
    burstCount: e.count,
    localHour: localHour(),
    dayOfWeek: dayOfWeek(),
  })
}

/** Flush every buffered transform burst into the send queue. */
function flushTransforms() {
  for (const key of Array.from(pending.keys())) firePending(key)
}

/**
 * A dnd-kit column drag, a resize, or fast toggling fires dozens of mutations.
 * Coalesce a burst of the same transform on the same report into ONE event
 * carrying a count, then let `enqueue` handle the rate cap.
 *
 * @param {{transform: string, reportId?: number|string, reportName?: string, detail?: string}} t
 */
export function trackTransform({ transform, reportId, reportName, detail } = {}) {
  if (!apiKey() || !initialized || !transform) return
  const key = `${transform}|${reportId}`
  const existing = pending.get(key)

  if (existing) {
    existing.count += 1
    // Last value wins: after a drag, the final position is the interesting one.
    if (detail !== undefined) existing.props.detail = detail
    clearTimeout(existing.timer)
    existing.timer = setTimeout(() => firePending(key), TRANSFORM_DEBOUNCE_MS)
    return
  }

  const entry = {
    count: 1,
    props: { transform, reportId, reportName, detail },
    timer: null,
  }
  pending.set(key, entry)
  entry.timer = setTimeout(() => firePending(key), TRANSFORM_DEBOUNCE_MS)
}

// ── Listeners ───────────────────────────────────────────────────────────────

/**
 * Bound from initPendo, never at module scope. With no API key this module adds
 * no globals at all, which is the property the env gate is supposed to have and
 * the one a top-level addEventListener would quietly break.
 *
 * `pagehide` rather than `unload`: `unload` is ignored under the back/forward
 * cache and does not fire reliably on mobile Safari.
 */
function bindListeners() {
  if (listenersBound || typeof window === 'undefined') return
  listenersBound = true
  window.addEventListener('pagehide', flushAll)
}

/** Test seam: exported so a suite can assert the buffer drained. */
export const __pendoInternals = {
  flushAll,
  pendingSize: () => pending.size,
  queueSize: () => queue.length,
}
